import { AlertTriangle, RefreshCw } from 'lucide-react';
import { ApiError } from '../../lib/api.js';
import { useI18n } from '../../i18n/index.js';

type Props = {
  /** What was being loaded — surfaces in the message: "שגיאה בטעינת {label}" */
  label?: string;
  /** Optional retry handler; if provided a button renders next to the message */
  onRetry?: () => void;
  /** Visual size — "inline" matches a row, "block" is a full-card replacement */
  variant?: 'inline' | 'block';
  /**
   * The error itself, when the caller has it.
   *
   * A refusal is not a network fault. The server sends its own Hebrew sentence
   * with a 403 — and this component threw it away and printed "בדוק את חיבור
   * הרשת" beside a retry button that could never succeed. Given the error, it
   * says what actually happened and withholds a retry that cannot help.
   */
  error?: unknown;
};

// Sprint 4 Medium #35 (2026-06-08 audit): every dashboard component that uses
// useQuery was ignoring isError, so a backend failure showed up as either a
// permanent spinner or a false "all clear" empty state. Use this component
// inside `if (isError) return <DataError ... />` branches so the manager
// gets a visible signal AND a retry path.
// ── ⚠ ומה שתוקן כאן 20/09/2026 ────────────────────────────────
//
// ארבע מחרוזות עבריות קשיחות ו-dir מקובע ל-rtl, ברכיב ש**53 קבצים**
// משתמשים בו — **19 מהם מסכים שתורגמו לרוסית**. כלומר המסך
// דיבר רוסית עד שמשהו נכשל, ואז הודעת השגיאה הגיעה בעברית —
// בדיוק ברגע שבו המשתמש הכי זקוק להבין מה קרה.
// ראו edal-i18n-child-components.
//
// ⚠ **ופער שנשאר פתוח:** `api.userMessage` מגיע מהשרת **בעברית
// בלבד**, גם למשתמש רוסי. הרכיב הזה אינו יכול לתקן את זה;
// מה שבשליטתו — הנוסח שלו עצמו — מתורגם. ראו edal-i18n-russian.
export function DataError({ label, onRetry, variant = 'inline', error }: Props): JSX.Element {
  const { t, dir } = useI18n();
  const api = error instanceof ApiError ? error : null;
  // A permission refusal carries its own sentence and cannot be retried.
  const forbidden = api?.status === 403 || api?.status === 401;
  const msg = api ? api.userMessage : label ? t('ui.errorLoadingLabel', { label }) : t('ui.errorLoading');
  const hint = forbidden ? t('ui.errorHintForbidden') : t('ui.errorHintNetwork');
  const retry = forbidden ? undefined : onRetry;
  if (variant === 'block') {
    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-2 py-8 px-4 text-center"
        dir={dir}
      >
        <AlertTriangle className="w-8 h-8 text-red-500" />
        <div className="text-sm font-medium text-slate-900">{msg}</div>
        <div className="text-xs text-slate-500">{hint}</div>
        {retry && (
          <button
            type="button"
            onClick={retry}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium rounded-md transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            {t('ui.retry')}
          </button>
        )}
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700"
      dir={dir}
    >
      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="flex-1">{msg}</span>
      {retry && (
        <button
          type="button"
          onClick={retry}
          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 underline-offset-2 hover:underline"
        >
          <RefreshCw className="w-3 h-3" />
          {t('ui.retry')}
        </button>
      )}
    </div>
  );
}
